import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Context ─────────────────────────────────────────────────────────────────
// Allows SheetContent (rendered as a child of Sheet) to close the panel via
// the same onOpenChange callback the parent passes to <Sheet>.
interface SheetContextValue {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}

const SheetContext = React.createContext<SheetContextValue>({
  open: false,
  onOpenChange: undefined,
});

const useSheet = () => React.useContext(SheetContext);

interface SheetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

/**
 * Sheet — slide-out panel component (shadcn-style).
 *
 * Renders a dismissible overlay + content panel. Closable via the close
 * button, overlay click, or the Escape key. The panel sits at z-50 so that
 * modals (Dialog at z-[100]) stack on top when opened from within the sheet.
 */
export function Sheet({ open = false, onOpenChange, children }: SheetProps) {
  // Close on Escape
  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange?.(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  if (open === false) return null;

  return (
    <SheetContext.Provider value={{ open, onOpenChange }}>
      {/* Overlay (z-50) — click outside closes */}
      <div
        className="fixed inset-0 z-50 bg-black/50"
        onClick={() => onOpenChange?.(false)}
        aria-hidden="true"
      />
      {children}
    </SheetContext.Provider>
  );
}

export function SheetContent({
  children,
  className,
  side = 'right',
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { side?: 'left' | 'right' | 'top' | 'bottom' }) {
  const { onOpenChange } = useSheet();

  const sideClasses =
    side === 'right'
      ? 'inset-y-0 right-0 border-l'
      : side === 'left'
        ? 'inset-y-0 left-0 border-r'
        : side === 'top'
          ? 'inset-x-0 top-0 border-b'
          : 'inset-x-0 bottom-0 border-t';

  return (
    <div
      className={cn(
        'fixed z-[60] bg-background shadow-lg',
        sideClasses,
        side === 'right' || side === 'left' ? 'h-full w-[400px]' : 'w-full',
        className,
      )}
      role="dialog"
      aria-modal="true"
      {...props}
    >
      {children}
      <button
        type="button"
        onClick={() => onOpenChange?.(false)}
        className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </button>
    </div>
  );
}

export function SheetHeader({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex flex-col space-y-2 border-b p-4', className)} {...props}>
      {children}
    </div>
  );
}

export function SheetTitle({ children, className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn('text-lg font-semibold', className)} {...props}>{children}</h2>;
}

export function SheetDescription({ children, className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props}>{children}</p>;
}

export function SheetTrigger({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" {...props}>{children}</button>;
}

export function SheetClose({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { onOpenChange } = useSheet();
  return (
    <button type="button" onClick={() => onOpenChange?.(false)} {...props}>
      {children}
    </button>
  );
}
